import type {CSSProperties} from 'react';
import {radius} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeOnboardChipsLook = 'chips-add' | 'chips-seed' | 'chips-remove';
export type WedgeOnboardChipsFieldVariant = 'wedge-chips-field' | 'wedge-chips-field-full';

const CHIPS_LOOKS = new Set<string>(['chips-add', 'chips-seed', 'chips-remove']);
export const isWedgeOnboardChipsLook = (look: string): look is WedgeOnboardChipsLook => CHIPS_LOOKS.has(look);
export const isWedgeOnboardChipsFieldVariant = (variant: unknown): variant is WedgeOnboardChipsFieldVariant => variant === 'wedge-chips-field' || variant === 'wedge-chips-field-full';

/* Released onboarding competitor chips (prep-screens-1 CompetitorChips, the Territory step). The name field grows on a flex line: 11px by 13px
   of padding, the surface fill, the card border, a 12px radius, 14px type, no outline of its own (the shipped onboarding field rules paint
   the focus ring from the caller class) and half opacity once the list is full. The Add button is a 44px square at least with a 12px radius,
   no border and 13px bold type: the primary ink on the page fill while a name is typed, the tertiary ink on the elevated fill while it is held
   off. A suggestion chip is a pill with 6px by 11px of padding, a 32px minimum height, the elevated fill, a hairline separator border and 12px
   semibold type. The remove button is a 44px square with no fill, border or padding; its negative margin pulls it into the chip, and the caller
   draws the 18px mark inside it. Each reads the exact legacy roles, and the declaration order is the order of the source. The caller owns the
   labels, the handlers, the shipped wedge-pill and wedge-pressable classes and every attribute. */
const FIELD_PADDING = '11px 13px', FIELD_FONT = 14, FULL_OPACITY = 0.5;
const ADD_PADDING = '11px 14px', TARGET = 44, ADD_FONT = 13;
const SEED_PADDING = '6px 11px', SEED_MIN = 32, SEED_FONT = 12;
const REMOVE_MARGIN = '-13px -13px -13px -8px';

export function wedgeOnboardChipsStyle(look: WedgeOnboardChipsLook, disabled = false): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'chips-seed') return {
    padding: SEED_PADDING, borderRadius: radius.pill, minHeight: SEED_MIN, background: r('bgElev'), border: `0.5px solid ${r('separatorBold')}`,
    color: r('labelSec'), fontSize: SEED_FONT, fontWeight: 600, fontFamily: r('font'), cursor: 'pointer',
  };
  if (look === 'chips-remove') return {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: TARGET, height: TARGET, margin: REMOVE_MARGIN,
    background: 'none', border: 0, padding: 0, cursor: 'pointer', fontFamily: r('font'),
  };
  return {
    padding: ADD_PADDING, minHeight: TARGET, minWidth: TARGET, boxSizing: 'border-box', borderRadius: radius.lg,
    background: disabled ? r('bgElev') : r('label'), color: disabled ? r('labelTer') : r('surface'),
    border: 0, cursor: disabled ? 'default' : 'pointer', fontSize: ADD_FONT, fontWeight: 700, fontFamily: r('font'),
  };
}

export function wedgeOnboardChipsFieldStyle(variant: WedgeOnboardChipsFieldVariant): CSSProperties {
  const r = wedgeExactRole;
  return {
    flex: 1, padding: FIELD_PADDING, background: r('surface'), border: `1px solid ${r('cardBorder')}`, borderRadius: radius.lg,
    fontSize: FIELD_FONT, color: r('label'), fontFamily: r('font'), outline: 0, boxSizing: 'border-box',
    opacity: variant === 'wedge-chips-field-full' ? FULL_OPACITY : 1,
  };
}

/* No look draws the transparent target. Add and remove are 44px already, so a region could only repeat the box. A suggestion chip is 32px tall
   and wraps 6px from the next row and the field row above, so the region of two chips would cover the same 6px gap; the hit grid of legacy
   has no such region. */
export const wedgeOnboardChipsDrawsHit = (look: string): boolean => !isWedgeOnboardChipsLook(look);
